import SubscriptionCard from "@/components/billing/SubscriptionCard/SubscriptionCard";
import PageContainer from "../container/PageContainer";
import BillingCard from "@/components/billing/BillingCard/BillingCard";
import Flex from "@/components/Flex/Flex";
import InvoicesTable from "@/components/billing/InvoiceTable/InvoiceTable";
import CancelModal from "@/components/billing/CancelModal/CancelModal";
import InvoiceModal from "@/components/billing/InvoiceModal/InvoiceModal";
import { useState, useEffect } from "react";
import { getSubscriptionsV2, getPaymentDetails, getInvoiceDetail, cancelSubscription, downloadInvoicePdf } from "@/api/billing";
import ResubscribeModal from "@/components/ResubscribeModal/ResubscribeModal";
import { type SubscriptionDetail, type PaymentMethodDetail, type Invoice, type InvoiceDetail, IntervalUnit, TermBehavior, CancellationReasons } from "@boxem/core-api-schema";
import ChangeSubscriptionModal, { ChangeSubscriptionModalMode } from "@/components/ChangeSubscriptionModal/ChangeSubscriptionModal";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { useUserContext } from "@/contexts/UserContext";
import BillingInformationModal from "@/components/billing/BillingInformationModal/BillingInformationModal";
import { RecurlyElements } from "@/components/RecurlyElements";

export default function BillingRecurlyPage() {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [isResubscribeModalOpen, setIsResubscribeModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentMethodDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUserContext();
  const [changeSubscriptionModalMode, setChangeSubscriptionModalMode] = useState<{mode: ChangeSubscriptionModalMode; deal: string}>({ mode: ChangeSubscriptionModalMode.closed, deal: ""});
  
  // Mock invoice data (still used for the invoice modal)
  const mockInvoiceData = {
    invoiceNumber: '10416',
    billedOn: 'Mar 25, 2025',
    terms: 'On-receipt',
    dueOn: 'Mar 25, 2025',
    
    companyName: 'Seven Tools LLC',
    companyAddress1: '936 SW 1st Ave # 377',
    companyAddress2: 'Miami, FL 33130',
    companyCountry: 'United States',
    companyEmail: 'support@boxem.com',
    
    billToName: 'Michael Auderer',
    billToAddress1: '8 The Green STE 4000',
    billToAddress2: 'Dover, DE 19904',
    billToCountry: 'United States',
    
    items: [
      {
        date: 'Mar 25 - Apr 25, 2025',
        description: 'Professional - Launch Special',
        quantity: 1,
        price: 69.99,
        discount: 69.99,
        total: 0.00
      }
    ],
    
    subtotal: 0.00,
    total: 0.00,
    paid: 0.00,
    amountDue: 0.00,
    
    notes: 'All amounts in United States Dollars (USD) Discounts Applied: 100% off'
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch subscription and payment details in parallel
        const [subscriptionData, paymentData] = await Promise.all([
          getSubscriptionsV2(),
          getPaymentDetails()
        ]);
        
        setSubscription(subscriptionData);
        setPaymentDetails(paymentData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching billing data:', err);
        setError('Failed to load billing data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      setSelectedInvoice(invoice);
      setInvoiceLoading(true);
      setIsInvoiceModalOpen(true); // Open modal immediately to show loading state
      
      // Fetch invoice details from API
      const detail = await getInvoiceDetail(invoice.id);
      setInvoiceDetail(detail);
      
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setError('Failed to load invoice details. Please try again.');
    } finally {
      setInvoiceLoading(false);
    }
  };
  
  const handlePrintInvoice = () => {
    window.print();
  };
  
  const handleDownloadPdf = async (invoice: Invoice) => {
    if (!invoice?.id) return;
    
    try {
      const blob = await downloadInvoicePdf(invoice.id);
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      setError('Failed to download invoice PDF. Please try again.');
    }
  };

  const handleUpdateSubscription = (subscriptionType: string, billingPeriod: string) => {
    setChangeSubscriptionModalMode({ 
      mode: ChangeSubscriptionModalMode.update, 
      deal: `${subscriptionType}-${billingPeriod}-launchspecial`
    });
  };
  
  const handleEditBillingInfo = () => {
    setIsBillingModalOpen(true);
  };

  const handleViewLatestInvoice = () => {
    if (subscription?.invoices && subscription.invoices.length > 0) {
      // Find the latest invoice by created date
      const latestInvoice = subscription.invoices.reduce((latest, current) => {
        return new Date(current.createdAt).getTime() > new Date(latest.createdAt).getTime() ? current : latest;
      });
      handleViewInvoice(latestInvoice);
    }
  };

  const handleCancelSubscription = async (reason: CancellationReasons, feedback?: string) => {
    if (!subscription?.id) {
      throw new Error('Subscription ID not found');
    }

    try {
      await cancelSubscription({
        subscriptionId: subscription.id,
        reason: reason,
        feedback: feedback
      });
      
      // Refresh subscription data after cancellation
      const updatedSubscription = await getSubscriptionsV2();
      setSubscription(updatedSubscription);
      
      // Close the modal
      setIsCancelModalOpen(false);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', color: 'red', marginTop: '2rem' }}>
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
        <ChangeSubscriptionModal
          targetPlanCode={changeSubscriptionModalMode.deal}
          mode={changeSubscriptionModalMode.mode}
          setMode={(v) => setChangeSubscriptionModalMode({ mode: v, deal: "" })}
          onClose={() => setChangeSubscriptionModalMode({mode: ChangeSubscriptionModalMode.closed, deal: ""})}
          onSuccess={async () => {
            // Refresh subscription data after successful upgrade
            try {
              const updatedSubscription = await getSubscriptionsV2();
              setSubscription(updatedSubscription);
            } catch (err) {
              console.error('Error refreshing subscription:', err);
            }
          }}
        />
        <Flex gap={4}>
            <Flex grow={1}>
                {subscription && (
                  <SubscriptionCard
                    plan={subscription.plan?.name}
                    isActive={subscription.state === 'active'}
                    state={subscription.state}
                    billingCycle={subscription.plan?.interval_unit}
                    currentPeriod={{
                      start: subscription.current_period_started_at ? new Date(subscription.current_period_started_at).toLocaleDateString() : '',
                      end: subscription.current_period_ends_at ? new Date(subscription.current_period_ends_at).toLocaleDateString() : ''
                    }}
                    termBehaviour={subscription.term_behavior === TermBehavior.AUTO_RENEW ? 'Auto Renewing' : 'Cancels at End of Term'}
                    collection={subscription.collection || 'Automatic'}
                    renewsOn={subscription.renews_on ? new Date(subscription.renews_on).toLocaleString() : ''}
                    startedOn={subscription.started_at ? new Date(subscription.started_at).toLocaleString() : ''}
                    latestInvoice={subscription.latestInvoice ? `#${subscription.latestInvoice}` : ''}
                    nextInvoiceDate={subscription.nextInvoice?.date ? new Date(subscription.nextInvoice.date).toLocaleDateString() : ''}
                    lineItems={[{
                      description: subscription.plan?.name || 'Subscription',
                      qty: 1,
                      price: subscription.nextInvoice?.amount || 0
                    }]}
                    currency={subscription.currency}
                    isThereNextTier={subscription.plan?.is_there_next_tier}
                    onUpgrade={handleUpdateSubscription}
                    onCancel={() => setIsCancelModalOpen(true)}
                    onResubscribe={() => setIsResubscribeModalOpen(true)}
                    handleViewInvoice={handleViewLatestInvoice}
                  />
                )}
            </Flex>
            <BillingCard 
              name={paymentDetails?.name || ''}
              cardType={paymentDetails?.creditCardType?.toLowerCase() || 'mastercard'}
              lastFour={paymentDetails?.creditCardLastFour || ''}
              expirationMonth={paymentDetails?.creditCardExpiration?.split('/')[0] || ''}
              expirationYear={paymentDetails?.creditCardExpiration?.split('/')[1] || ''}
              address={paymentDetails?.address || ''}
              city={paymentDetails?.city || ''}
              state={paymentDetails?.state || ''}
              zipCode={paymentDetails?.zipCode || ''}
              country={paymentDetails?.country || ''}
              startedOn={subscription?.started_at ? new Date(subscription.started_at).toLocaleString() : ''}
              onEdit={handleEditBillingInfo}
            />
        </Flex>
        <InvoicesTable 
          invoices={subscription?.invoices || []}
          isLoading={loading}
          onView={handleViewInvoice}
          onDownload={handleDownloadPdf}
        />
        <CancelModal
          open={isCancelModalOpen}
          onOpenChange={setIsCancelModalOpen}
          onCancel={() => setIsCancelModalOpen(false)}
          onKeep={() => setIsCancelModalOpen(false)}
          billingEndDate={subscription?.current_period_ends_at ? new Date(subscription.current_period_ends_at).toLocaleDateString() : "2024-12-31"}
          submitCancellationReason={handleCancelSubscription}
        />
        <InvoiceModal
          open={isInvoiceModalOpen}
          onOpenChange={setIsInvoiceModalOpen}
          onClose={() => setIsInvoiceModalOpen(false)}
          onPrint={handlePrintInvoice}
          onDownloadPdf={() => selectedInvoice && handleDownloadPdf(selectedInvoice)}
          isLoading={invoiceLoading}
          
          // Map from InvoiceDetail to InvoiceModal props (only used when not loading)
          invoiceNumber={invoiceDetail?.invoiceNumber || ''}
          billedOn={invoiceDetail?.billedOn || ''}
          terms={invoiceDetail?.terms || ''}
          dueOn={invoiceDetail?.dueOn || ''}
          
          companyName={invoiceDetail?.companyName || ''}
          companyAddress1={invoiceDetail?.companyAddress?.street1 || ''}
          companyAddress2={invoiceDetail ? `${invoiceDetail.companyAddress.city}, ${invoiceDetail.companyAddress.region} ${invoiceDetail.companyAddress.postalCode}` : ''}
          companyCountry={invoiceDetail?.companyAddress?.country || ''}
          companyEmail={invoiceDetail?.companyEmail || ''}
          
          billToName={invoiceDetail?.customerName || ''}
          billToAddress1={invoiceDetail?.customerAddress?.street1 || ''}
          billToAddress2={invoiceDetail ? `${invoiceDetail.customerAddress.city}, ${invoiceDetail.customerAddress.region} ${invoiceDetail.customerAddress.postalCode}` : ''}
          billToCountry={invoiceDetail?.customerAddress?.country || ''}
          
          items={invoiceDetail?.lineItems || []}
          
          subtotal={invoiceDetail?.subtotal || 0}
          total={invoiceDetail?.total || 0}
          paid={invoiceDetail?.paid || 0}
          amountDue={invoiceDetail?.amountDue || 0}
          
          notes={invoiceDetail ? `All amounts in ${invoiceDetail.currency} ${invoiceDetail.discountApplied ? `Discounts Applied: ${invoiceDetail.discountApplied}` : ''}` : ''}
        />
        <RecurlyElements>
          <BillingInformationModal 
            open={isBillingModalOpen} 
            onOpenChange={setIsBillingModalOpen}
            billingInfoId={paymentDetails?.id} // TODO: Get actual billing info ID from API
            onSuccess={() => {
              // Refresh payment details after successful update
              getPaymentDetails().then(setPaymentDetails).catch(console.error);
            }}
            initialData={paymentDetails ? {
              firstName: paymentDetails.name?.split(' ')[0] || '',
              lastName: paymentDetails.name?.split(' ').slice(1).join(' ') || '',
              address1: paymentDetails.address || '',
              address2: '', // Payment details doesn't have address2
              city: paymentDetails.city || '',
              state: paymentDetails.state || '',
              postalCode: paymentDetails.zipCode || '',
              country: paymentDetails.country || 'US',
            } : undefined}
          />
        </RecurlyElements>
        <ResubscribeModal
          open={isResubscribeModalOpen}
          onOpenChange={setIsResubscribeModalOpen}
          onReactivate={async (planCode) => {
            console.log('Reactivating with plan:', planCode);
            setIsResubscribeModalOpen(false);
            
            // Refresh subscription data after reactivation
            try {
              const updatedSubscription = await getSubscriptionsV2();
              setSubscription(updatedSubscription);
            } catch (err) {
              console.error('Error refreshing subscription after reactivation:', err);
            }
          }}
          onEditBilling={handleEditBillingInfo}
          currentPlan={{
            name: subscription?.plan?.name || '',
            code: subscription?.plan?.code || ''
          }}
          currentSubscriptionId={subscription?.id}
          billingInfo={{
            lastFour: paymentDetails?.creditCardLastFour || '',
            cardType: paymentDetails?.creditCardType?.toLowerCase() || 'mastercard',
            name: paymentDetails?.name || '',
            expirationMonth: paymentDetails?.creditCardExpiration?.split('/')[0] || '',
            expirationYear: paymentDetails?.creditCardExpiration?.split('/')[1] || '',
            address: paymentDetails?.address || '',
            city: paymentDetails?.city || '',
            state: paymentDetails?.state || '',
            zipCode: paymentDetails?.zipCode || '',
            country: paymentDetails?.country || '',
            startedOn: subscription?.started_at ? new Date(subscription.started_at).toLocaleString() : ''
          }}
        />
    </PageContainer>
  )
}
